// Thin client for the Projects endpoints (global-D1 CRUD + chat membership). Bearer auth via the
// shell token, matching the rest of the Ros API. Keeps fetch/JSON plumbing out of the pages.

import type { SessionArtifact } from '../components/rosArtifacts'

export interface ProjectListItem {
  id: string
  name: string
  description: string | null
  createdAt: string
  updatedAt: string
  chatCount: number
  artifactCount: number
  lastActive: number        // ms epoch
}

// A member chat as resolved by the detail endpoint (summary only — content stays in the DO).
export interface ProjectChat {
  id: string
  intentSummary?: string
  startedAt: number
  lastActiveAt?: number
  messageCount: number
  panelType?: string
}

export interface ProjectDetail {
  project: { id: string; name: string; description: string | null; createdAt: string; updatedAt: string }
  chats: ProjectChat[]
  artifacts: SessionArtifact[]
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

const base = (apiBaseUrl: string) => `${apiBaseUrl}/api/r3/projects`

export async function listProjects(apiBaseUrl: string, token: string | null): Promise<ProjectListItem[]> {
  const res = await fetch(base(apiBaseUrl), { credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`projects ${res.status}`)
  const data = await res.json() as { projects: ProjectListItem[] }
  return data.projects ?? []
}

export async function getProject(apiBaseUrl: string, token: string | null, id: string): Promise<ProjectDetail> {
  const res = await fetch(`${base(apiBaseUrl)}/${id}`, { credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`project ${res.status}`)
  return await res.json() as ProjectDetail
}

export async function createProject(apiBaseUrl: string, token: string | null, name: string, description?: string): Promise<ProjectListItem['id']> {
  const res = await fetch(base(apiBaseUrl), {
    method: 'POST', credentials: 'include', headers: authHeaders(token),
    body: JSON.stringify({ name, description }),
  })
  if (!res.ok) throw new Error(`create ${res.status}`)
  const data = await res.json() as { project: { id: string } }
  return data.project.id
}

export async function updateProject(apiBaseUrl: string, token: string | null, id: string, patch: { name?: string; description?: string }): Promise<void> {
  const res = await fetch(`${base(apiBaseUrl)}/${id}`, {
    method: 'PATCH', credentials: 'include', headers: authHeaders(token), body: JSON.stringify(patch),
  })
  if (!res.ok) throw new Error(`update ${res.status}`)
}

export async function deleteProject(apiBaseUrl: string, token: string | null, id: string): Promise<void> {
  const res = await fetch(`${base(apiBaseUrl)}/${id}`, { method: 'DELETE', credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`delete ${res.status}`)
}

export async function addChatToProject(apiBaseUrl: string, token: string | null, projectId: string, sessionId: string): Promise<void> {
  const res = await fetch(`${base(apiBaseUrl)}/${projectId}/sessions/${sessionId}`, { method: 'POST', credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`addChat ${res.status}`)
}

export async function removeChatFromProject(apiBaseUrl: string, token: string | null, projectId: string, sessionId: string): Promise<void> {
  const res = await fetch(`${base(apiBaseUrl)}/${projectId}/sessions/${sessionId}`, { method: 'DELETE', credentials: 'include', headers: authHeaders(token) })
  if (!res.ok) throw new Error(`removeChat ${res.status}`)
}
